/* */
import { OemSatellite } from '@app/app/objects/oem-satellite';
import { ToastMsgType } from '@app/engine/core/interfaces';
import { keepTrackApi } from '@app/keepTrackApi';
import { SelectSatManager } from '@app/plugins/select-sat-manager/select-sat-manager';
import { SettingsMenuPlugin } from '@app/plugins/settings-menu/settings-menu';
import { SettingsManager } from '@app/settings/settings';
import { OrbitCruncherType, OrbitDrawTypes } from '@app/webworker/orbitCruncher';
import { mat4 } from 'gl-matrix';
import { CameraType } from '../camera/camera';
import { GetSatType } from '../core/interfaces';
import { ServiceLocator } from '../core/service-locator';
import { EventBus } from '../events/event-bus';
import { EventBusEvent } from '../events/event-bus-events';
import { errorManagerInstance } from '../utils/errorManager';
import { setInnerHtml } from '../utils/get-el';
import { isThisNode } from '../utils/isThisNode';
export class OrbitManager {
    currentInView_ = [];
    currentSelectId_ = -1;
    glBuffers_ = [];
    gl_;
    hoverOrbitBuf_;
    inProgress_ = [];
    isInitialized_ = false;
    lineManagerInstance_;
    secondaryOrbitBuf_;
    secondarySelectId_ = -1;
    selectOrbitBuf_;
    updateAllThrottle_ = 0;
    orbitCache_ = new Map();
    orbitWorker;
    playNextSatellite = null;
    tempTransColor = [0, 0, 0, 0];
    addInViewOrbit(satId) {
        for (const inViewSatId of this.currentInView_) {
            if (satId === inViewSatId) {
                return;
            }
        }
        this.currentInView_.push(satId);
        this.updateOrbitBuffer(satId);
    }
    clearHoverOrbit() {
        if (!settingsManager.isDrawOrbits) {
            return;
        }
        const gl = this.gl_ ?? keepTrackApi.getRenderer().gl;
        gl.bindBuffer(gl.ARRAY_BUFFER, this.hoverOrbitBuf_);
        gl.bufferData(gl.ARRAY_BUFFER, new Float32Array((settingsManager.orbitSegments + 1) * 4), gl.DYNAMIC_DRAW);
    }
    clearInViewOrbit() {
        if (this.currentInView_.length === 0) {
            return;
        }
        this.currentInView_ = [];
    }
    clearSelectOrbit(isSecondary = false) {
        const gl = this.gl_ ?? keepTrackApi.getRenderer().gl;
        if (isSecondary) {
            this.secondarySelectId_ = -1;
            if (!this.secondaryOrbitBuf_) {
                return;
            }
            gl.bindBuffer(gl.ARRAY_BUFFER, this.secondaryOrbitBuf_);
        }
        else {
            this.currentSelectId_ = -1;
            if (!this.selectOrbitBuf_) {
                return;
            }
            gl.bindBuffer(gl.ARRAY_BUFFER, this.selectOrbitBuf_);
        }
        gl.bufferData(gl.ARRAY_BUFFER, new Float32Array((settingsManager.orbitSegments + 1) * 4), gl.DYNAMIC_DRAW);
    }
    draw(projectionCameraMatrix, tgtBuffer, hoverManagerInstance, colorSchemeManagerInstance, mainCameraInstance) {
        if (!this.isInitialized_) {
            return;
        }
        const gl = this.gl_ ?? keepTrackApi.getRenderer().gl;
        const selectSatManagerInstance = keepTrackApi.getPlugin(SelectSatManager);
        gl.bindFramebuffer(gl.FRAMEBUFFER, tgtBuffer);
        gl.useProgram(this.lineManagerInstance_.program);
        if (settingsManager.isDrawOrbits) {
            gl.enable(gl.BLEND);
            if (settingsManager.showOrbitThroughEarth) {
                gl.disable(gl.DEPTH_TEST);
            }
            else {
                gl.enable(gl.DEPTH_TEST);
            }
            const modelViewMatrix = mat4.create();
            // Default to 1 so no transformation
            mat4.identity(modelViewMatrix);
            this.lineManagerInstance_.setWorldUniforms(modelViewMatrix, projectionCameraMatrix);
            this.drawGroupObjectOrbit(hoverManagerInstance, colorSchemeManagerInstance);
            this.drawInViewObjectOrbit_(mainCameraInstance);
            this.drawPrimaryObjectOrbit_();
            this.drawSecondaryObjectOrbit_();
            this.drawHoverObjectOrbit_(hoverManagerInstance, colorSchemeManagerInstance);
        }
        gl.disable(gl.BLEND);
        gl.enable(gl.DEPTH_TEST);
        if (settingsManager.enableConstantSelectedSatRedraw) {
            if ((selectSatManagerInstance?.selectedSat ?? -1) > -1) {
                this.clearSelectOrbit(false);
                this.setSelectOrbit(selectSatManagerInstance?.selectedSat ?? -1, false);
            }
            if ((selectSatManagerInstance?.secondarySat ?? -1) > -1) {
                this.clearSelectOrbit(true);
                this.setSelectOrbit(selectSatManagerInstance?.secondarySat ?? -1, true);
            }
        }
    }
    drawOrbitsSettingChanged() {
        // We may have skipped initialization on boot and now need to do it
        if (!this.isInitialized_) {
            this.init(keepTrackApi.getLineManager(), keepTrackApi.getRenderer().gl);
        }
    }
    init(lineManagerInstance, gl, orbitWorker) {
        if (this.isInitialized_) {
            return;
        } // Only initialize once
        if (!settingsManager.isDrawOrbits) {
            return;
        }
        if (!settingsManager.colors) {
            return;
        }
        this.lineManagerInstance_ = lineManagerInstance;
        this.gl_ = gl;
        this.tempTransColor = settingsManager.colors.transparent;
        const catalogManagerInstance = keepTrackApi.getCatalogManager();
        // See if we are running jest right now for testing
        this.startCruncher_(orbitWorker);
        this.orbitWorker.onmessage = this.workerOnMessage_.bind(this);
        this.selectOrbitBuf_ = this.gl_.createBuffer();
        this.gl_.bindBuffer(this.gl_.ARRAY_BUFFER, this.selectOrbitBuf_);
        this.gl_.bufferData(this.gl_.ARRAY_BUFFER, new Float32Array((settingsManager.orbitSegments + 1) * 4), this.gl_.DYNAMIC_DRAW);
        this.secondaryOrbitBuf_ = this.gl_.createBuffer();
        this.gl_.bindBuffer(this.gl_.ARRAY_BUFFER, this.secondaryOrbitBuf_);
        this.gl_.bufferData(this.gl_.ARRAY_BUFFER, new Float32Array((settingsManager.orbitSegments + 1) * 4), this.gl_.DYNAMIC_DRAW);
        this.hoverOrbitBuf_ = this.gl_.createBuffer();
        this.gl_.bindBuffer(this.gl_.ARRAY_BUFFER, this.hoverOrbitBuf_);
        this.gl_.bufferData(this.gl_.ARRAY_BUFFER, new Float32Array((settingsManager.orbitSegments + 1) * 4), this.gl_.DYNAMIC_DRAW);
        // Allocate one buffer for every satellite
        for (let i = 0; i < catalogManagerInstance.missileSats; i++) {
            this.glBuffers_.push(this.allocateBuffer());
        }
        const objDataString = OrbitManager.getObjDataString(keepTrackApi.getCatalogManager().objectCache);
        if (!this.orbitWorker) {
            return;
        }
        this.orbitWorker.postMessage({
            typ: OrbitCruncherType.INIT,
            orbitFadeFactor: settingsManager.orbitFadeFactor,
            objData: objDataString,
            numSegs: settingsManager.orbitSegments,
        });
        this.isInitialized_ = true;
        EventBus.getInstance().on(EventBusEvent.KeyDown, (key, _code, isRepeat) => {
            if (!isRepeat) {
                switch (key) {
                    case 'L':
                        this.toggleOrbitLines_();
                        SettingsMenuPlugin.syncOnLoad();
                        SettingsManager.preserveSettings();
                        break;
                    case 'E':
                        this.toggleEciToEcf_();
                        SettingsMenuPlugin.syncOnLoad();
                        SettingsManager.preserveSettings();
                        break;
                    default:
                        break;
                }
            }
        });
        EventBus.getInstance().on(EventBusEvent.highPerformanceRender, () => {
            this.updateAllVisibleOrbits();
        });
        keepTrackApi.emit(EventBusEvent.orbitManagerInit);
    }
    toggleOrbitLines_() {
        settingsManager.isDrawOrbits = !settingsManager.isDrawOrbits;
        if (settingsManager.isDrawOrbits) {
            keepTrackApi.getUiManager().toast('Orbits On', ToastMsgType.normal);
        }
        else {
            keepTrackApi.getUiManager().toast('Orbits Off', ToastMsgType.standby);
        }
    }
    toggleEciToEcf_() {
        settingsManager.isOrbitCruncherInEcf = !settingsManager.isOrbitCruncherInEcf;
        if (settingsManager.isOrbitCruncherInEcf) {
            keepTrackApi.getUiManager().toast('GEO Orbits displayed in ECF', ToastMsgType.normal);
        }
        else {
            keepTrackApi.getUiManager().toast('GEO Orbits displayed in ECI', ToastMsgType.standby);
        }
    }
    startCruncher_(orbitWorker) {
        if (isThisNode()) {
            if (typeof orbitWorker !== 'undefined') {
                this.orbitWorker = orbitWorker;
            }
            else {
                this.orbitWorker = {
                    postMessage: () => {
                        // This is intentional
                    },
                };
            }
        }
        else {
            if (typeof Worker === 'undefined') {
                throw new Error('Your browser does not support web workers.');
            }
            try {
                this.orbitWorker = new Worker('./js/orbitCruncher.js');
            }
            catch (error) {
                // If you are trying to run this off the desktop you might have forgotten --allow-file-access-from-files
                if (window.location.href.startsWith('file://')) {
                    setInnerHtml('loader-text', 'Critical Error: You need to allow access to files from your computer! Ensure "--allow-file-access-from-files" is added to your chrome shortcut and that no other ' +
                        'copies of chrome are running when you start it.');
                }
                else {
                    errorManagerInstance.error(error, 'OrbitManager.init', 'Failed to create orbit web worker!');
                }
            }
        }
    }
    removeInViewOrbit(satId) {
        let r = null;
        for (let i = 0; i < this.currentInView_.length; i++) {
            if (satId === this.currentInView_[i]) {
                r = i;
            }
        }
        if (r === null) {
            return;
        }
        this.currentInView_.splice(r, 1);
        this.updateOrbitBuffer(satId);
    }
    setHoverOrbit(satId) {
        this.updateOrbitBuffer(satId);
    }
    setSelectOrbit(satId, isSecondary = false) {
        if (isSecondary) {
            this.secondarySelectId_ = satId;
        }
        else {
            this.currentSelectId_ = satId;
        }
        this.updateOrbitBuffer(satId);
    }
    updateAllVisibleOrbits() {
        const uiManagerInstance = keepTrackApi.getUiManager();
        if (uiManagerInstance.searchManager?.isResultsOpen && !settingsManager.disableUI && !settingsManager.lowPerf) {
            const currentSearchSats = uiManagerInstance.searchManager.getLastResultGroup()?.ids;
            if (typeof currentSearchSats !== 'undefined') {
                if (this.updateAllThrottle_ >= currentSearchSats.length) {
                    this.updateAllThrottle_ = 0;
                }
                for (let i = 0; this.updateAllThrottle_ < currentSearchSats.length && i < 5; this.updateAllThrottle_++, i++) {
                    this.updateOrbitBuffer(currentSearchSats[this.updateAllThrottle_]);
                }
            }
        }
    }
    changeOrbitBufferData(id, tle1, tle2) {
        const timeManagerInstance = keepTrackApi.getTimeManager();
        if (!this.orbitWorker) {
            return;
        }
        this.orbitWorker.postMessage({
            typ: OrbitCruncherType.SATELLITE_UPDATE,
            id,
            dynamicOffsetEpoch: timeManagerInstance.dynamicOffsetEpoch,
            staticOffset: timeManagerInstance.staticOffset,
            rate: timeManagerInstance.propRate,
            tle1,
            tle2,
            isEcfOutput: settingsManager.isOrbitCruncherInEcf,
        });
    }
    updateOrbitBuffer(id, missileParams) {
        const catalogManagerInstance = keepTrackApi.getCatalogManager();
        const timeManagerInstance = keepTrackApi.getTimeManager();
        const obj = catalogManagerInstance.getObject(id, GetSatType.EXTRA_ONLY);
        if (!obj) {
            return;
        }
        if (!settingsManager.isDrawOrbits) {
            return;
        }
        if (!this.inProgress_[id] && !obj.isStatic()) {
            if (obj.isMissile()) {
                if (!this.orbitWorker) {
                    return;
                }
                this.orbitWorker.postMessage({
                    typ: OrbitCruncherType.MISSILE_UPDATE,
                    id,
                    dynamicOffsetEpoch: timeManagerInstance.dynamicOffsetEpoch,
                    staticOffset: timeManagerInstance.staticOffset,
                    propRate: timeManagerInstance.propRate,
                    // If we are updating a missile trajectory, we need to pass in the missile params
                    latList: missileParams?.latList,
                    lonList: missileParams?.lonList,
                    altList: missileParams?.altList,
                });
            }
            else if (obj instanceof OemSatellite) {
                this.setOemSatelliteOrbitBuffer_(id, obj.getOrbitPath(settingsManager.oemOrbitSegments));
            }
            else {
                // Then it is a satellite
                if (!this.orbitWorker) {
                    return;
                }
                this.orbitWorker.postMessage({
                    typ: OrbitCruncherType.SATELLITE_UPDATE,
                    id,
                    dynamicOffsetEpoch: timeManagerInstance.dynamicOffsetEpoch,
                    staticOffset: timeManagerInstance.staticOffset,
                    propRate: timeManagerInstance.propRate,
                    isEcfOutput: settingsManager.isOrbitCruncherInEcf,
                });
                this.inProgress_[id] = true;
            }
        }
    }
    static getObjDataString(objData) {
        return JSON.stringify(
        // TODO: objData should always be guaranteed
        objData?.map((obj) => {
            if (!obj.isSatellite() && !obj.isMissile()) {
                return { ignore: true };
            }
            if (obj.isMissile()) {
                return { missile: true };
            }
            return {
                tle1: obj.tle1,
                tle2: obj.tle2,
            };
        }));
    }
    allocateBuffer(bufferLength = (settingsManager.orbitSegments + 1) * 4) {
        const gl = this.gl_ ?? keepTrackApi.getRenderer().gl;
        const buf = gl.createBuffer();
        gl.bindBuffer(gl.ARRAY_BUFFER, buf);
        gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(bufferLength), gl.DYNAMIC_DRAW);
        return buf;
    }
    // private isCalculateColorLocked = false;
    drawGroupObjectOrbit(hoverManagerInstance, colorSchemeManagerInstance) {
        const groupManagerInstance = keepTrackApi.getGroupsManager();
        const colorData = colorSchemeManagerInstance.colorData;
        if (groupManagerInstance.selectedGroup !== null && !settingsManager.isGroupOverlayDisabled) {
            groupManagerInstance.selectedGroup.ids.forEach((id) => {
                if (id === hoverManagerInstance.getHoverId() || id === this.currentSelectId_) {
                    return;
                } // Skip hover and select objects
                if (!keepTrackApi.getCatalogManager().getObject(id)?.active) {
                    return;
                } // Skip inactive objects
                OrbitManager.checkColorBuffersValidity_(id, colorData);
                // if color is black, we probably have old data, so recalculate color buffers
                if (settingsManager.isShowLeoSats &&
                    settingsManager.isShowMeoSats &&
                    settingsManager.isShowGeoSats &&
                    settingsManager.isShowHeoSats &&
                    colorData[id * 4] <= 0 &&
                    colorData[id * 4 + 1] <= 0 &&
                    colorData[id * 4 + 2] <= 0) {
                    // eslint-disable-next-line no-debugger
                    // debugger;
                    // if (this.isCalculateColorLocked) {
                    //   /*
                    //    * Prevent unexpected infinite coloring loop!
                    //    * TODO: This should be explored more to see if there is a better way to handle this
                    //    */
                    //   return;
                    // }
                    // colorSchemeManagerInstance.calculateColorBuffers(true);
                    // // Fix: Crued workaround to getting dots to show up when loading with search parameter
                    // setTimeout(() => {
                    //   colorSchemeManagerInstance.calculateColorBuffers(true);
                    //   this.isCalculateColorLocked = true;
                    // }, 500);
                    // setTimeout(() => {
                    //   this.isCalculateColorLocked = false;
                    // }, 2000);
                }
                if (colorData[id * 4 + 3] <= 0) {
                    return; // Skip transparent objects
                    /*
                     * Debug: This is useful when all objects are supposed to be visible but groups can filter out objects
                     * throw new Error(`color buffer for ${id} isn't visible`);
                     */
                }
                this.lineManagerInstance_.setColorUniforms([colorData[id * 4], colorData[id * 4 + 1], colorData[id * 4 + 2], colorData[id * 4 + 3] * settingsManager.orbitGroupAlpha]);
                this.writePathToGpu_(id);
            });
        }
    }
    drawHoverObjectOrbit_(hoverManagerInstance, colorSchemeManagerInstance) {
        if (settingsManager.isMobileModeEnabled) {
            return;
        } // No hover orbit on mobile
        const hoverId = hoverManagerInstance.getHoverId();
        if (hoverId !== -1 && hoverId !== this.currentSelectId_ && !keepTrackApi.getCatalogManager().getObject(hoverId, GetSatType.EXTRA_ONLY)?.isStatic()) {
            OrbitManager.checkColorBuffersValidity_(hoverId, colorSchemeManagerInstance.colorData);
            this.lineManagerInstance_.setColorUniforms(settingsManager.orbitHoverColor);
            this.writePathToGpu_(hoverId);
        }
    }
    static checkColorBuffersValidity_(hoverId, colorData) {
        const hoverIdIndex = hoverId * 4;
        OrbitManager.checkColorBufferValidity_(hoverIdIndex, colorData);
        OrbitManager.checkColorBufferValidity_(hoverIdIndex + 1, colorData);
        OrbitManager.checkColorBufferValidity_(hoverIdIndex + 2, colorData);
        OrbitManager.checkColorBufferValidity_(hoverIdIndex + 3, colorData);
    }
    static checkColorBufferValidity_(index, colorData) {
        if (typeof colorData[index] === 'undefined') {
            throw new Error(`color buffer for ${index / 4} not valid`);
        }
    }
    drawInViewObjectOrbit_(mainCameraInstance) {
        if (this.currentInView_.length >= 1) {
            // There might be some z-fighting
            if (mainCameraInstance.cameraType === CameraType.PLANETARIUM) {
                this.lineManagerInstance_.setColorUniforms(settingsManager.orbitPlanetariumColor);
            }
            else {
                this.lineManagerInstance_.setColorUniforms(settingsManager.orbitInViewColor);
            }
            this.currentInView_.forEach((id) => {
                this.writePathToGpu_(id);
            });
        }
    }
    drawPrimaryObjectOrbit_() {
        if (this.currentSelectId_ !== -1 && !keepTrackApi.getCatalogManager().getObject(this.currentSelectId_, GetSatType.EXTRA_ONLY)?.isStatic()) {
            this.lineManagerInstance_.setColorUniforms(settingsManager.orbitSelectColor);
            this.writePathToGpu_(this.currentSelectId_);
        }
    }
    drawSecondaryObjectOrbit_() {
        if (this.secondarySelectId_ !== -1 && !keepTrackApi.getCatalogManager().getObject(this.secondarySelectId_, GetSatType.EXTRA_ONLY)?.isStatic()) {
            this.lineManagerInstance_.setColorUniforms(settingsManager.orbitSelectColor2);
            this.writePathToGpu_(this.secondarySelectId_);
        }
    }
    workerOnMessage_(m) {
        const satId = m.data.satId;
        const pointsOut = new Float32Array(m.data.pointsOut);
        const gl = this.gl_ ?? keepTrackApi.getRenderer().gl;
        gl.bindBuffer(gl.ARRAY_BUFFER, this.glBuffers_[satId]);
        gl.bufferData(gl.ARRAY_BUFFER, pointsOut, gl.DYNAMIC_DRAW);
        this.inProgress_[satId] = false;
        this.orbitCache_.set(satId, pointsOut);
    }
    setOemSatelliteOrbitBuffer_(satId, pointsOut) {
        const gl = this.gl_ ?? keepTrackApi.getRenderer().gl;
        gl.bindBuffer(gl.ARRAY_BUFFER, this.glBuffers_[satId]);
        // get current buffer size
        const currentBufferSize = gl.getBufferParameter(gl.ARRAY_BUFFER, gl.BUFFER_SIZE);
        if (currentBufferSize !== pointsOut.byteLength) {
            // reallocate buffer if size has changed
            gl.bufferData(gl.ARRAY_BUFFER, pointsOut, gl.DYNAMIC_DRAW);
        }
        else {
            // update buffer data
            gl.bufferSubData(gl.ARRAY_BUFFER, 0, pointsOut);
        }
        this.inProgress_[satId] = false;
    }
    /**
     * Updates the orbit data for a satellite by shifting its position relative
     * to the new first point. This compensates for floating point precision issues
     * when dealing with large coordinate values.
     */
    alignOrbitSelectedObject(satId, firstPosition, isShiftFirstOnly = false) {
        let satOrbitData;
        if (this.orbitCache_.has(satId)) {
            satOrbitData = this.orbitCache_.get(satId) ?? null;
        }
        else {
            satOrbitData = this.getBufferData(satId);
            if (satOrbitData) {
                this.orbitCache_.set(satId, satOrbitData);
            }
        }
        if (!satOrbitData) {
            return;
        }
        const deltaOfFirstPoint = [
            firstPosition[0] - satOrbitData[0],
            firstPosition[1] - satOrbitData[1],
            firstPosition[2] - satOrbitData[2],
        ];
        for (let i = 0; i < (isShiftFirstOnly ? 4 : satOrbitData.length); i += 4) {
            satOrbitData[i] += deltaOfFirstPoint[0];
            satOrbitData[i + 1] += deltaOfFirstPoint[1];
            satOrbitData[i + 2] += deltaOfFirstPoint[2];
            // Ignore oppacity
        }
        const gl = this.gl_ ?? keepTrackApi.getRenderer().gl;
        gl.bindBuffer(gl.ARRAY_BUFFER, this.glBuffers_[satId]);
        // get current buffer size
        // DEBUG: Disabled reallocation check for now to see if it helps with performance
        // const currentBufferSize = gl.getBufferParameter(gl.ARRAY_BUFFER, gl.BUFFER_SIZE);
        // if (currentBufferSize !== satOrbitData.byteLength) {
        //   // Delete the buffer first
        //   gl.deleteBuffer(this.glBuffers_[satId]);
        //   // Create a new buffer
        //   this.glBuffers_[satId] = this.allocateBuffer(settingsManager.oemOrbitSegments * 4);
        //   gl.bindBuffer(gl.ARRAY_BUFFER, this.glBuffers_[satId]);
        //   // reallocate buffer if size has changed
        //   gl.bufferData(gl.ARRAY_BUFFER, satOrbitData, gl.DYNAMIC_DRAW);
        // } else {
        //   // update buffer data
        //   gl.bufferSubData(gl.ARRAY_BUFFER, 0, satOrbitData);
        // }
        gl.bufferSubData(gl.ARRAY_BUFFER, 0, satOrbitData);
    }
    /** Returns the current data from the buffer for the given satId. */
    getBufferData(satId) {
        const gl = this.gl_ ?? keepTrackApi.getRenderer().gl;
        const buffer = this.glBuffers_[satId];
        if (!buffer) {
            return null;
        }
        gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
        const currentBufferSize = gl.getBufferParameter(gl.ARRAY_BUFFER, gl.BUFFER_SIZE);
        // WebGL2 only: getBufferSubData copies buffer data into an ArrayBufferView
        const out = new Float32Array(currentBufferSize / 4);
        gl.getBufferSubData(gl.ARRAY_BUFFER, 0, out);
        return out;
    }
    writePathToGpu_(id) {
        if (id === -1) {
            return;
        } // no hover object
        if (typeof this.glBuffers_[id] === 'undefined') {
            throw new Error(`orbit buffer ${id} not allocated`);
        }
        const obj = ServiceLocator.getCatalogManager().getObject(id, GetSatType.EXTRA_ONLY);
        if (!obj) {
            return;
        }
        if (obj instanceof OemSatellite && obj.orbitPathCache_) {
            this.lineManagerInstance_.setAttribsAndDrawLineStrip(this.glBuffers_[id], settingsManager.oemOrbitSegments);
        }
        else {
            this.lineManagerInstance_.setAttribsAndDrawLineStrip(this.glBuffers_[id], settingsManager.orbitSegments + 1);
        }
    }
    updateOrbitType() {
        if (!this.orbitWorker) {
            return;
        }
        if (settingsManager.isDrawTrailingOrbits) {
            keepTrackApi.getOrbitManager().orbitWorker.postMessage({
                typ: OrbitCruncherType.CHANGE_ORBIT_TYPE,
                orbitType: OrbitDrawTypes.TRAIL,
            });
        }
        else {
            keepTrackApi.getOrbitManager().orbitWorker.postMessage({
                typ: OrbitCruncherType.CHANGE_ORBIT_TYPE,
                orbitType: OrbitDrawTypes.ORBIT,
            });
        }
    }
}
